import React, { PureComponent, SyntheticEvent } from 'react';
import { Button } from 'Common/components/Atoms/Button';
import { ContentLoader } from 'Common/components/Atoms/ContentLoader';
import { Div } from 'Common/components/Atoms/Div';
import { Flex } from 'Common/components/Atoms/Flex';
import { FontIcon } from 'Common/components/Atoms/FontIcon';
import { InputField } from 'Common/components/Atoms/InputField';
import { Text } from 'Common/components/Atoms/Text';
import { actions as signupsActions } from '../../reducers/signups';
import { actions as accountActions } from '../../reducers/Account';
import {
  accountUserSelector,
  isLoadingAccountSelector,
} from 'selectors/Account';
import { connect } from 'react-redux';
import { Action, bindActionCreators, Dispatch } from 'redux';
import { InputLabel } from '../ReviewerSignUpProfile';
import StepCard from '../../components/StepCard';
import { Theme } from '@mui/material/styles';
import withTheme, { WithTheme } from '@mui/styles/withTheme';
import withStyles, { WithStyles } from '@mui/styles/withStyles';
import {
  faTimes,
  faFileInvoice,
  faPlus,
  faGlobeAmericas,
} from '@fortawesome/pro-solid-svg-icons';
import { faGithub, faLinkedin } from '@fortawesome/free-brands-svg-icons';
import { User } from '../../models/User';
import {
  isValidLinkedinUrl,
  extractGithubProfileName,
} from '../../utils/urlHelpers';
import { logEvent, REVIEWER_APPLICATION_LINKS } from 'utils/analytics';

const styles = (theme: Theme) => ({
  additionalLink: {
    borderRadius: theme.shape.borderRadius,
    background: theme.palette.grey[100],
    padding: '5px 12px',
    marginTop: '8px',
    cursor: 'pointer',
    '&:hover': {
      background: theme.palette.grey[200],
    },
  },
  remove: {
    '&:hover': {
      color: theme.palette.common.black,
    },
  },
  input: {
    '& div.MuiInputBase-root': {
      fontSize: theme.typography.fontSize,
    },
    '& div.Mui-disabled': {
      backgroundColor: theme.palette.grey[100],
      color: theme.palette.text.primary,
      '& fieldset': {
        border: 'none',
      },
    },
  },
  linkStyle: {
    color: theme.palette.secondary.main,
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.secondary.dark,
    },
  },
});

const GITHUB_URL_PREFIX = 'https://github.com/';

export interface Props {
  user: User;
  updateReviewerProfile: (
    github: string,
    linkedin: string,
    file?: File,
    additional?: string[]
  ) => void;
  forceNextStep: () => void;
  theme: Theme;
  classes: {
    [key: string]: string;
  };
  isUpdating: boolean;
  isLoadingAccount: boolean;
  error?: string;
}

interface State {
  github?: string;
  linkedin?: string;
  additionalLinks: string[];
  errors?: {
    github?: boolean;
    linkedin?: boolean;
  };
  resume?: string;
  file?: File;
  tempAdditionalLink?: string;
}

export class ReviewerProfileLinks extends PureComponent<Props, State> {
  state: State = {
    errors: {},
    github: this.props.user?.reviewer_profile?.github_username
      ? GITHUB_URL_PREFIX + this.props.user?.reviewer_profile?.github_username
      : '',
    linkedin: this.props.user?.reviewer_profile?.linked_in_url || '',
    resume: undefined,
    additionalLinks:
      this.props.user?.reviewer_profile?.additional_info?.links || [],
    tempAdditionalLink: undefined,
  };

  componentDidMount() {
    logEvent(REVIEWER_APPLICATION_LINKS);
  }

  handleSubmit = () => {
    const { updateReviewerProfile, forceNextStep, user } = this.props;
    const { github, linkedin, additionalLinks, file } = this.state;
    const validLinkedinUrl = isValidLinkedinUrl(linkedin);
    const githubProfileName = extractGithubProfileName(github);

    if (!githubProfileName || !linkedin || !validLinkedinUrl) {
      this.setState({
        errors: {
          github: !githubProfileName,
          linkedin: !validLinkedinUrl,
        },
      });

      return;
    }

    if (
      githubProfileName !== user.reviewer_profile?.github_username ||
      linkedin !== user.reviewer_profile?.linked_in_url ||
      additionalLinks !== user.reviewer_profile?.additional_info?.links ||
      file
    ) {
      updateReviewerProfile(githubProfileName, linkedin, file, additionalLinks);
    } else {
      forceNextStep();
    }
  };

  handleChange = (name: string, value: string) => {
    const { errors } = this.state;
    this.setState({
      [name]: value,
      errors: { ...errors, [name]: undefined },
    } as Pick<State, keyof State>);
  };

  handleFileChange = (files: FileList | null) => {
    this.setState({
      resume: files ? files[0].name : '',
      file: files ? files[0] : ({} as File),
    });
  };

  addAdditionalLink = () => {
    const { tempAdditionalLink, additionalLinks } = this.state;
    if (!tempAdditionalLink) {
      return false;
    }
    if (additionalLinks.includes(tempAdditionalLink)) {
      this.setState({ tempAdditionalLink: '' });
    } else {
      this.setState({
        additionalLinks: [...additionalLinks, tempAdditionalLink],
        tempAdditionalLink: undefined,
      });
    }
  };

  removeAdditionalLink = (e: SyntheticEvent, url: string) => {
    e.stopPropagation();
    this.setState({
      additionalLinks: this.state.additionalLinks.filter((val) => val !== url),
    });
  };

  render(): JSX.Element {
    const { theme, classes, isUpdating, isLoadingAccount } = this.props;
    const {
      errors,
      github,
      linkedin,
      resume,
      additionalLinks,
      tempAdditionalLink,
    } = this.state;
    return (
      <StepCard
        showPreviousButton={false}
        handleSubmit={this.handleSubmit}
        isUpdating={isUpdating}
      >
        <Div mb={3}>
          <InputLabel>Github Profile URL</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="38px" width="100%" />
          ) : (
            <InputField
              id="github"
              data-testid="qa-github"
              width="100%"
              className={classes.input}
              placeholder="https://github.com/your-username"
              value={github}
              error={errors?.github}
              helperText={
                errors?.github && 'Please provide a valid Github profile URL'
              }
              onChange={(event) =>
                this.handleChange('github', event.target.value)
              }
              startIcon={
                <FontIcon
                  icon={faGithub}
                  color={theme.palette.text.secondary}
                />
              }
            />
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>LinkedIn Profile URL</InputLabel>
          {isLoadingAccount ? (
            <ContentLoader height="38px" width="100%" />
          ) : (
            <InputField
              id="linkedin"
              data-testid="qa-linkedin"
              width="100%"
              className={classes.input}
              placeholder="https://linkedin.com/in/your-username"
              value={linkedin}
              error={errors?.linkedin}
              helperText={
                errors?.linkedin &&
                'Please provide a valid LinkedIn profile URL'
              }
              onChange={(event) =>
                this.handleChange('linkedin', event.target.value)
              }
              startIcon={
                <FontIcon
                  icon={faLinkedin}
                  color={theme.palette.text.secondary}
                />
              }
            />
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>Additional Links (optional)</InputLabel>
          <InputField
            id="additionalLinks"
            data-testid="qa-links"
            width="100%"
            value={tempAdditionalLink || ''}
            className={classes.input}
            onChange={(event) =>
              this.handleChange('tempAdditionalLink', event.target.value)
            }
            inputProps={{
              onKeyUp: (e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                  this.addAdditionalLink();
                }
              },
            }}
            startIcon={
              <FontIcon
                icon={faGlobeAmericas}
                color={theme.palette.text.secondary}
              />
            }
            endIcon={
              <Button
                id="addLink"
                variant="contained"
                color="primary"
                width={125}
                height={25}
                fontSize="12px"
                mr={1}
                onClick={this.addAdditionalLink}
                disabled={!tempAdditionalLink}
              >
                <Text
                  variant="body1"
                  fontSize="12px"
                  color={theme.palette.common.white}
                >
                  <FontIcon icon={faPlus} size="1x" mr={1} />
                  Add Link
                </Text>
              </Button>
            }
          />
          {additionalLinks.map((url, key) => (
            <Div
              data-url={url}
              key={url}
              className={classes.additionalLink}
              id={`additional-link-${key}`}
              onClick={() => window.open(url, '_blank')}
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text variant="body1">{url}</Text>
                <FontIcon
                  icon={faTimes}
                  size="1x"
                  color={theme.palette.text.secondary}
                  title="Remove URL"
                  cursor="pointer"
                  className={classes.remove}
                  onClick={(e) => this.removeAdditionalLink(e, url)}
                />
              </Flex>
            </Div>
          ))}
          {additionalLinks.length === 0 && (
            <Text mt={1} variant="body2" color="textSecondary">
              e.g. personal website, StackOverflow, Quora.
            </Text>
          )}
        </Div>
        <Div mb={3}>
          <InputLabel>Resume (optional)</InputLabel>
          <InputField
            id="resume"
            data-testid="qa-resume"
            width="100%"
            className={classes.input}
            value={resume || ''}
            placeholder="Upload a PDF"
            readOnly
            startIcon={
              <FontIcon
                icon={faFileInvoice}
                color={theme.palette.text.secondary}
              />
            }
            endIcon={
              <label htmlFor="fileInput">
                <Text
                  variant="body1"
                  mr="15px"
                  color="secondary"
                  component="span"
                  className={classes.linkStyle}
                >
                  Select a file
                </Text>
              </label>
            }
          />
          <input
            style={{ display: 'none' }}
            type="file"
            accept=".pdf"
            id="fileInput"
            onChange={(event) =>
              this.handleFileChange(event.currentTarget.files)
            }
          />
        </Div>
      </StepCard>
    );
  }
}

function mapStateToProps(state: any) {
  const { signups } = state;
  const user = accountUserSelector(state);
  const isLoadingAccount = isLoadingAccountSelector(state);
  return {
    isUpdating: signups.isUpdating,
    isLoadingAccount,
    user,
    error: signups.error,
  };
}

function mapDispatchToProps(dispatch: Dispatch<Action>) {
  return bindActionCreators({ ...signupsActions, ...accountActions }, dispatch);
}

export default withStyles(styles)(
  withTheme<Theme, React.JSXElementConstructor<Props>>(
    connect(mapStateToProps, mapDispatchToProps)(ReviewerProfileLinks)
  ) as any
);
